import { useState } from "react";
import {
  X,
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  Pill,
  Calendar,
  Bell,
  TrendingUp,
} from "lucide-react";

interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  targetElement?: string;
  content: React.ReactNode;
}

interface OnboardingWizardProps {
  isVisible: boolean;
  onComplete: () => void;
  onSkip: () => void;
}

export function OnboardingWizard({
  isVisible,
  onComplete,
  onSkip,
}: OnboardingWizardProps) {
  const [currentStep, setCurrentStep] = useState(0);

  const steps: OnboardingStep[] = [
    {
      id: "welcome",
      title: "Welcome to Pill Scheduler!",
      description: "Your personal supplement scheduling assistant",
      icon: <Pill className="text-blue-600" size={32} />,
      content: (
        <div className="text-center space-y-4">
          <div className="bg-blue-50 rounded-2xl p-6">
            <Pill className="text-blue-600 mx-auto mb-4" size={48} />
            <h3 className="text-lg font-bold text-gray-900 mb-2">
              Smart Supplement Scheduling
            </h3>
            <p className="text-gray-600 text-sm leading-relaxed">
              Take the guesswork out of supplement timing. We'll help you create
              a personalized schedule that optimizes absorption and prevents
              interactions.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4 text-xs">
            <div className="bg-green-50 rounded-lg p-3">
              <CheckCircle className="text-green-600 mx-auto mb-2" size={20} />
              <div className="font-medium text-green-900">100+ Presets</div>
              <div className="text-green-700">Ready-to-use supplements</div>
            </div>
            <div className="bg-purple-50 rounded-lg p-3">
              <Bell className="text-purple-600 mx-auto mb-2" size={20} />
              <div className="font-medium text-purple-900">Smart Reminders</div>
              <div className="text-purple-700">Never miss a dose</div>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: "presets",
      title: "Discover 100+ Built-in Supplements",
      description: "Browse our extensive library of pre-configured supplements",
      icon: <Calendar className="text-green-600" size={32} />,
      targetElement: '[data-tour="presets-tab"]',
      content: (
        <div className="space-y-4">
          <div className="bg-green-50 rounded-xl p-4">
            <Calendar className="text-green-600 mb-3" size={32} />
            <h3 className="font-bold text-gray-900 mb-2">
              Ready-to-Use Library
            </h3>
            <p className="text-gray-600 text-sm mb-3">
              We've pre-configured 100+ supplements with optimal timing, food
              relationships, and priority levels based on scientific research.
            </p>
            <ul className="text-xs text-gray-600 space-y-1">
              <li>• Essential vitamins (D3, B-Complex, C)</li>
              <li>• Minerals (Magnesium, Zinc, Iron)</li>
              <li>• Specialized nutrients (Omega-3, Probiotics)</li>
              <li>• Herbal supplements (Ashwagandha, Turmeric)</li>
            </ul>
          </div>
          <div className="text-center">
            <div className="inline-flex items-center gap-2 text-sm text-blue-600 bg-blue-50 px-3 py-2 rounded-lg">
              💡 You can also create custom supplements for anything not in our
              library
            </div>
          </div>
        </div>
      ),
    },
    {
      id: "schedule",
      title: "Build Your Daily Schedule",
      description:
        "Create a personalized routine with smart conflict resolution",
      icon: <Calendar className="text-purple-600" size={32} />,
      targetElement: '[data-tour="schedule-tab"]',
      content: (
        <div className="space-y-4">
          <div className="bg-purple-50 rounded-xl p-4">
            <Calendar className="text-purple-600 mb-3" size={32} />
            <h3 className="font-bold text-gray-900 mb-2">Smart Scheduling</h3>
            <p className="text-gray-600 text-sm mb-3">
              Our intelligent scheduler automatically handles timing conflicts
              and optimizes absorption based on food relationships.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div className="bg-white border border-purple-200 rounded-lg p-3">
              <div className="font-medium text-purple-900 mb-1">Gap Rules</div>
              <div className="text-gray-600">
                Automatic spacing between incompatible supplements
              </div>
            </div>
            <div className="bg-white border border-purple-200 rounded-lg p-3">
              <div className="font-medium text-purple-900 mb-1">
                Priority System
              </div>
              <div className="text-gray-600">
                Important supplements get optimal timing
              </div>
            </div>
          </div>
          <div className="text-center">
            <div className="inline-flex items-center gap-2 text-sm text-purple-600 bg-purple-50 px-3 py-2 rounded-lg">
              ⚡ Example: Iron taken 2+ hours away from calcium for better
              absorption
            </div>
          </div>
        </div>
      ),
    },
    {
      id: "notifications",
      title: "Set Up Smart Reminders",
      description: "Never miss a dose with intelligent push notifications",
      icon: <Bell className="text-orange-600" size={32} />,
      targetElement: '[data-tour="settings-tab"]',
      content: (
        <div className="space-y-4">
          <div className="bg-orange-50 rounded-xl p-4">
            <Bell className="text-orange-600 mb-3" size={32} />
            <h3 className="font-bold text-gray-900 mb-2">
              Intelligent Reminders
            </h3>
            <p className="text-gray-600 text-sm mb-3">
              Get personalized notifications with action buttons to quickly mark
              doses as taken, snooze, or missed.
            </p>
          </div>
          <div className="bg-white border border-orange-200 rounded-lg p-3">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                <Pill size={16} className="text-blue-600" />
              </div>
              <div className="flex-1">
                <div className="font-medium text-gray-900 text-sm">
                  Time for Vitamin D3!
                </div>
                <div className="text-xs text-gray-500">
                  Take your morning dose now
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <button className="flex-1 bg-green-100 text-green-800 rounded-lg py-2 text-xs font-medium">
                ✓ Taken
              </button>
              <button className="flex-1 bg-orange-100 text-orange-800 rounded-lg py-2 text-xs font-medium">
                ⏰ Snooze 15m
              </button>
              <button className="flex-1 bg-red-100 text-red-800 rounded-lg py-2 text-xs font-medium">
                ✗ Missed
              </button>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: "dashboard",
      title: "Track Your Progress",
      description: "Monitor streaks, earn badges, and stay motivated",
      icon: <TrendingUp className="text-pink-600" size={32} />,
      targetElement: '[data-tour="dashboard-tab"]',
      content: (
        <div className="space-y-4">
          <div className="bg-pink-50 rounded-xl p-4">
            <TrendingUp className="text-pink-600 mb-3" size={32} />
            <h3 className="font-bold text-gray-900 mb-2">Stay Motivated</h3>
            <p className="text-gray-600 text-sm mb-3">
              Track your consistency with streaks, earn achievement badges, and
              learn with daily supplement facts.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-2 text-xs">
            <div className="bg-white border border-pink-200 rounded-lg p-2 text-center">
              <div className="text-lg mb-1">🔥</div>
              <div className="font-medium text-pink-900">Streaks</div>
              <div className="text-gray-600">Daily consistency</div>
            </div>
            <div className="bg-white border border-pink-200 rounded-lg p-2 text-center">
              <div className="text-lg mb-1">🏆</div>
              <div className="font-medium text-pink-900">Badges</div>
              <div className="text-gray-600">Achievements</div>
            </div>
            <div className="bg-white border border-pink-200 rounded-lg p-2 text-center">
              <div className="text-lg mb-1">💡</div>
              <div className="font-medium text-pink-900">Tips</div>
              <div className="text-gray-600">Daily facts</div>
            </div>
          </div>
          <div className="text-center bg-gradient-to-r from-pink-50 to-purple-50 rounded-lg p-3">
            <div className="text-sm font-medium text-gray-900 mb-1">
              🎉 You're ready to start your supplement journey!
            </div>
            <div className="text-xs text-gray-600">
              Begin by browsing presets or building your first schedule
            </div>
          </div>
        </div>
      ),
    },
  ];

  const currentStepData = steps[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === steps.length - 1;

  const handleNext = () => {
    if (isLastStep) {
      onComplete();
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleStepClick = (stepIndex: number) => {
    setCurrentStep(stepIndex);
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-60 z-50" />

      {/* Main Modal */}
      <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 text-white relative">
            <button
              onClick={onSkip}
              className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
            >
              <X size={24} />
            </button>

            <div className="flex items-center gap-3 mb-4">
              {currentStepData.icon}
              <div>
                <h2 className="text-xl font-bold">{currentStepData.title}</h2>
                <p className="text-blue-100 text-sm">
                  {currentStepData.description}
                </p>
              </div>
            </div>

            {/* Progress Indicator */}
            <div className="flex gap-2">
              {steps.map((_, index) => (
                <button
                  key={index}
                  onClick={() => handleStepClick(index)}
                  className={`flex-1 h-2 rounded-full transition-all duration-300 ${
                    index <= currentStep
                      ? "bg-white"
                      : "bg-white/30 hover:bg-white/50"
                  }`}
                />
              ))}
            </div>

            <div className="text-center mt-2">
              <span className="text-blue-100 text-sm">
                Step {currentStep + 1} of {steps.length}
              </span>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">{currentStepData.content}</div>

          {/* Footer */}
          <div className="border-t border-gray-200 p-4 flex items-center justify-between bg-gray-50">
            <button
              onClick={onSkip}
              className="text-gray-500 hover:text-gray-700 text-sm font-medium transition-colors"
            >
              Skip Tutorial
            </button>

            <div className="flex gap-3">
              {!isFirstStep && (
                <button
                  onClick={handlePrev}
                  className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  <ArrowLeft size={16} />
                  Previous
                </button>
              )}

              <button
                onClick={handleNext}
                className="flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-medium hover:from-blue-700 hover:to-purple-700 transition-all duration-200 transform hover:scale-105"
              >
                {isLastStep ? (
                  <>
                    Get Started
                    <CheckCircle size={16} />
                  </>
                ) : (
                  <>
                    Next
                    <ArrowRight size={16} />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
